import React, { useState, useEffect } from 'react';
import PropTypes from "prop-types";
import Rating from "./Rating";
import Api from "../../services/Api";
import { useSelector } from "react-redux";
import Notifications from "../../services/Notifications";
import { CSSTransition } from "react-transition-group";
import useDelayedRender from "../../hooks/useDelayedRender";
import MUIModal from "../shared/MUIModal";
import ActionButton from "../shared/button/ActionButton";
import { Typography } from "@mui/material";

const titleStyles = {
    fontWeight: 700,
    fontSize: "2.5rem",
    fontFamily: "Gotham XNarrow SSm A, Gotham XNarrow SSm B",
};

export default function Survey({ localized, feature }) {
    const [surveyFrequency, setSurveyFrequency] = useState({ showSurvey: false });
    const [open, setOpen] = useState(true);
    const [selectedRating, setSelectedRating] = useState(null);
    const [errors, setErrors] = useState({});
    const [formState, setFormState] = useState({
        selectedRating: 0,
        missingInformation: false,
        incorrectInformation: false,
        notEnoughDetail: false,
        outOfDateInformation: false,
        other: false,
        specify: "",
    });

    const store = useSelector((state) => state);
    const { shouldRender } = useDelayedRender(1500);

    useEffect(() => {
        const fetchSurveyFrequency = async () => {
            try {
                const response = await Api.fetch(`/api/survey/GetSurveyFrequency/${feature}`, {
                    method: "GET",
                    headers: { "Content-Type": "application/json" },
                });
                if (response?.showSurvey !== undefined) {
                    setSurveyFrequency({ showSurvey: response.showSurvey === 1 });
                } else {
                    Notifications.error(localized.FailedSurveyFrequency);
                }
            } catch {
                Notifications.error(localized.FailedSurveyFrequency);
            }
        };
        fetchSurveyFrequency();
    }, []);

    const validateOtherText = (value) => value.trim() === "";

    const handleClose = (_, reason) => {
        if (reason === "backdropClick") return;
        setOpen(false);
        setErrors({});
    };

    const handleChange = (value) => {
        setFormState((prev) => ({ ...prev, specify: value }));
        setErrors((prev) => ({ ...prev, specify: validateOtherText(value) }));
    };

    const handleFormSubmit = async (event) => {
        event.preventDefault();
        const otherTextError = validateOtherText(formState.specify);
        if (formState.other && otherTextError) {
            setErrors({ specify: otherTextError });
            return;
        }

        const response = await Api.fetch("api/survey/create", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                userId: store.user.currentUser.id,
                customerId: store.user.currentUser.selectedCustomerId,
                rating: formState.selectedRating,
                missingInformation: formState.missingInformation,
                incorrectInformation: formState.incorrectInformation,
                notEnoughDetail: formState.notEnoughDetail,
                outOfDateInformation: formState.outOfDateInformation,
                other: formState.other,
                OtherText: formState.specify,
                feature,
            }),
        });

        if (!response.ok) {
            Notifications.error(localized.FailedSaveSurvey);
            return;
        }

        handleClose();
        Notifications.message(localized.SavedSurvey);
    };

    const updateRating = (rating) => {
        if (rating === null) rating = formState.selectedRating;
        setSelectedRating(rating);
        setFormState((prev) => ({
            ...prev,
            selectedRating: rating,
            other: rating === 1 || rating === 2,
            specify: "",
        }));
        if (rating >= 3 && rating <= 5) saveSurvey(rating);
    };

    const saveSurvey = async (rating) => {
        const response = await Api.fetch("api/survey/create", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                userId: store.user.currentUser.id,
                customerId: store.user.currentUser.selectedCustomerId,
                rating,
                feature,
            }),
        });

        if (!response.ok) {
            Notifications.error(localized.FailedSaveSurvey);
        } else {
            Notifications.message(localized.SavedSurvey);
        }
    };

    return (
        <MUIModal
            open={shouldRender && open && surveyFrequency.showSurvey}
            onClose={handleClose}
            title={
                !selectedRating
                    ? localized.SurveyTitle
                    : selectedRating === 1 || selectedRating === 2
                        ? localized.TellUsWhy
                        : ""
            }
            onSubmit={handleFormSubmit}
            maxWidth="sm"
            fullWidth
            closeLabel={localized.Close}
            customActions={
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "2rem",
                        paddingBottom: "2rem",
                    }}
                >
                    {!(selectedRating >= 3 && selectedRating <= 5) ? (
                        <>
                            {selectedRating ? (
                                <ActionButton
                                    type="button"
                                    variant="gray-outline"
                                    text={localized.ButtonText_Back}
                                    onClick={() => setSelectedRating(null)}
                                />
                            ) : null}
                            <ActionButton
                                type={selectedRating ? "submit" : "button"}
                                variant="blue-solid"
                                text={selectedRating ? localized.Send : localized.Dismiss}
                                onClick={!selectedRating ? handleClose : null}
                            />
                        </>
                    ) : (
                        <ActionButton
                            type="button"
                            variant="blue-solid"
                            text={localized.Dismiss}
                            onClick={handleClose}
                        />
                    )}
                </div>
            }
        >
            <div
                style={{
                    paddingTop: 0,
                    paddingBottom: "4rem",
                    display: "flex",
                    justifyContent: "center",
                }}
            >
                {!selectedRating ? (
                    <Rating
                        localized={localized}
                        setSelectedRating={updateRating}
                        rating={formState.selectedRating}
                    />
                ) : (
                    <div className="d-flex flex-column align-items-center w-100 pt-4">
                        {selectedRating >= 3 && selectedRating <= 5 && (
                            <span
                                style={{
                                    textAlign: "center",
                                    margin: "0 auto",
                                    ...titleStyles,
                                }}
                            >
                                {localized.ThankYouFeedback}
                            </span>
                        )}
                        <CSSTransition
                            in={selectedRating === 1 || selectedRating === 2}
                            timeout={300}
                            classNames="fadeTransition"
                            unmountOnExit
                        >
                            <>
                                <textarea
                                    name="specify"
                                    className={`form-control ${errors.specify ? "is-invalid" : ""
                                        }`}
                                    style={{
                                        resize: "none",
                                        height: "130px",
                                        fontSize: "1.8rem",
                                        marginTop: "3rem",
                                    }}
                                    onChange={(event) =>
                                        event.target.value.length <= 255 &&
                                        handleChange(event.target.value)
                                    }
                                    value={formState.specify}
                                />
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        width: "100%",
                                    }}
                                >
                                    <p
                                        style={{
                                            color: "#dc3545",
                                            alignSelf: "flex-start",
                                            margin: 0,
                                            visibility: errors.specify ? "visible" : "hidden",
                                        }}
                                    >
                                        {localized.PleaseSpecify}
                                    </p>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            paddingTop: "0.5rem",
                                            fontFamily: "Gotham XNarrow SSm A, Gotham XNarrow SSm B",
                                        }}
                                    >
                                        {formState.specify.length} / 255
                                    </Typography>
                                </div>
                            </>
                        </CSSTransition>
                    </div>
                )}
            </div>
        </MUIModal>
    );
}

Survey.propTypes = {
    localized: PropTypes.object,
    feature: PropTypes.string,
};
